<template>
  <div>
    <app-modal content-class="col-4 col-xl-3 p-0" @close="close" overflow>
      <b-card no-body>
        <b-card-body class="pb-2" :title="isNew ? 'Create new user' : 'Update user'">
          <b-form-group
            label="Name*"
            label-cols="3"
            label-align="right"
            :state="!$v.user.name.$error"
            :disabled="ownerEditDisabled || userMutationsDisabled"
          >
            <b-input :value="user.name" @change="update('name', $event)" />
            <template #invalid-feedback>
              <div v-if="!$v.user.name.required">User name required</div>
              <div v-if="!$v.user.name.serverErrors">
                {{ getServerFieldErrors('name') }}
              </div>
            </template>
          </b-form-group>
          <b-form-group
            label="Email*"
            label-cols="3"
            label-align="right"
            :state="!$v.user.email.$error"
            :disabled="ownerEditDisabled || userMutationsDisabled"
          >
            <b-input :value="user.email" @change="update('email', $event)" />
            <template #invalid-feedback>
              <div v-if="!$v.user.email.required">Email required</div>
              <div v-if="!$v.user.email.serverErrors">
                {{ getServerFieldErrors('email') }}
              </div>
            </template>
          </b-form-group>
          <b-form-group
            v-if="!ownerEditDisabled"
            label="Password*"
            label-cols="3"
            label-align="right"
            :disabled="userMutationsDisabled"
            :state="!$v.user.password.$error"
            valid-feedback="Don't forget to copy password. It's will unavailable after close."
          >
            <b-input-group>
              <b-input :value="user.password" @keyup="update('password', $event)" />
              <template #append>
                <b-btn
                  variant="default"
                  class="password-copy"
                  :disabled="!user.password"
                  title="Copy password to clipboard"
                  @click="copyPassword"
                  ><i class="fa fa-copy"></i
                ></b-btn>
              </template>
            </b-input-group>
            <template #invalid-feedback>
              <div v-if="!$v.user.email.requiredIf">
                You have to set password for a new user
              </div>
              <div v-if="!$v.user.password.serverErrors">
                {{ getServerFieldErrors('password') }}
              </div>
            </template>
          </b-form-group>
          <b-form-group
            v-if="!isNew"
            label="Status"
            label-cols="3"
            label-align="right"
            :disabled="statusEditDisabled || userMutationsDisabled"
            :state="!$v.user.status.$error"
          >
            <b-select :value="user.status" @change="update('status', $event)" :options="{ 1: 'Inactive', 10: 'Active' }" />
            <template #invalid-feedback>
              <div v-if="!$v.user.status.serverErrors">
                {{ getServerFieldErrors('status') }}
              </div>
            </template>
          </b-form-group>
          <b-form-group
            label="Role"
            label-cols="3"
            label-align="right"
            :disabled="roleEditDisabled || userMutationsDisabled"
            :state="!$v.user.role.$error"
          >
            <b-select :value="user.role" @change="setRole" :options="roleOptions" />
            <template #invalid-feedback>
              <div v-if="!$v.user.role.serverErrors">
                {{ getServerFieldErrors('role') }}
              </div>
            </template>
          </b-form-group>
        </b-card-body>
        <b-card-footer class="d-flex">
          <b-btn v-if="!isEditingUserOwner && !isNew && !isEditingYourOwn && !userMutationsDisabled" variant="default" @click="destroy"
            >Delete</b-btn
          >
          <div class="ml-auto"></div>
          <b-btn v-if="!ownerEditDisabled && !userMutationsDisabled" variant="primary mr-2" @click="save">Save</b-btn>&nbsp;
          <b-btn variant="default" @click="close">Close</b-btn>
        </b-card-footer>
      </b-card>
    </app-modal>
  </div>
</template>

<script>
import store from '@/store'
import { USER_CREATE, USER_UPDATE } from '@/store/modules/user'
import { isLocalId } from '@/utils/Id'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { convertServerErrors } from '@/utils/Helpers'

export default {
  name: 'User',
  data: () => ({
    buildingEditorEnabled: false,
    userId: null,
    serverErrors: {},
    isLogoutRequired: false
  }),
  validations() {
    return {
      user: {
        name: {
          required,
          serverErrors: function() {
            return !this.getServerFieldErrors('name')
          }
        },
        email: {
          required,
          serverErrors: function() {
            return !this.getServerFieldErrors('email')
          }
        },
        password: {
          requiredIf: requiredIf(() => this.isNew),
          serverErrors: function() {
            return !this.getServerFieldErrors('password')
          }
        },
        status: {
          serverErrors: function() {
            return !this.getServerFieldErrors('status')
          }
        },
        role: {
          serverErrors: function() {
            return !this.getServerFieldErrors('role')
          }
        }
      }
    }
  },
  computed: {
    ...mapState('auth', { signedUser: 'user' }),
    ...mapGetters('user', ['getUser']),
    ...mapGetters('settings', ['scimUsersSyncEnabled']),
    userMutationsDisabled() {
      return this.scimUsersSyncEnabled && this.user.source === 'scim'
    },
    user() {
      return this.getUser(this.userId) || {}
    },
    isSignedUserOwner() {
      return this.signedUser.role === 'superAdminRole'
    },
    isEditingUserOwner() {
      return this.user.role === 'superAdminRole'
    },
    isNew() {
      return isLocalId(this.userId)
    },
    isEditingYourOwn() {
      return this.signedUser.id === this.user.id
    },
    ownerEditDisabled() {
      return this.isEditingUserOwner && !this.isSignedUserOwner
    },
    statusEditDisabled() {
      return this.isEditingYourOwn || this.ownerEditDisabled
    },
    roleEditDisabled() {
      return this.isEditingYourOwn || this.ownerEditDisabled
    },
    roleOptions() {
      const options = { adminRole: 'Admin', userRole: 'User' }
      if (!this.isNew && (this.isEditingUserOwner || this.isSignedUserOwner)) {
        options.superAdminRole = 'Owner'
      }
      return options
    }
  },
  methods: {
    ...mapMutations('user', { updateLocal_: USER_UPDATE }),
    ...mapActions('user', ['userSave', 'userDestroy', 'userLoad']),
    ...mapActions('auth', ['logout']),
    setRole(newRole) {
      this.isLogoutRequired = newRole === 'superAdminRole'
      this.update('role', newRole)
    },
    /**
     * @param fieldName
     * @returns {string} merged error messages splited by br tag
     */
    getServerFieldErrors(fieldName) {
      if (this.serverErrors && this.serverErrors[fieldName]) {
        return this.serverErrors[fieldName].join('<br>')
      }
      return ''
    },
    buildingEditorToggle() {
      this.buildingEditorEnabled = !this.buildingEditorEnabled
    },
    update(k, v) {
      let value = v instanceof Event ? v.target.value : v
      this.updateLocal_({ id: this.user.id, [k]: value })
      this.$v.user[k] && this.$v.user[k].$touch()
    },
    copyPassword() {
      if (this.$clipboard(this.user.password)) {
        this.$appToast.success('Password copied successfully.')
      } else {
        this.$appToast.error('Error on password copy.')
      }
    },
    close() {
      this.$router.push({ name: 'users' })
    },
    save(close = true) {
      this.$v.$touch()
      this.serverErrors = {}
      if (!this.$v.$invalid) {
        if (this.isNew) {
          this.update('status', 10)
        }
        return this.userSave(this.user).then((fId) => {
            this.password && this.copyPassword()
            if (this.isLogoutRequired) {
              this.logout()
              window.location.reload()
            } else {
              close && this.close()
              this.userId = fId
              return fId
            }
          }, (error) => {
            if (error.response && error.response.status === 422) {
              this.serverErrors = convertServerErrors(error.response.data)
            }
          })
      }
    },
    destroy() {
      this.userDestroy(this.user).then(() => {
        this.$router.push({ name: 'users' })
      })
    }
  },
  created() {
    this.userId = this.$route.params.userId
  },
  beforeRouteEnter(to, from, next) {
    // create local user
    let uId = to.params.userId
    if (isLocalId(uId) && !store.getters['user/getUser'](uId)) {
      store.commit(`user/${USER_CREATE}`, { id: uId })
    } else {
      store.dispatch(`user/userLoad`, { id: uId })
    }
    next()
  }
}
</script>

<style lang="scss">
.password-copy {
  cursor: pointer;
}
</style>
